// Display formatting for the V2 screens. All copy is English, so dates are
// formatted with the en-US locale, in the restaurant's IANA zone when known
// (the same zone the bootstrap renders every other reservation label in).

/** "$14", "$14.50". The Paper artboards drop ".00" on whole dollars. */
export function formatPrice(cents: number): string {
  const safe = Number.isFinite(cents) ? Math.max(0, Math.round(cents)) : 0;
  const dollars = safe / 100;
  return safe % 100 === 0 ? `$${dollars}` : `$${dollars.toFixed(2)}`;
}

/** An itemized option's amount, always with cents, as PAT shows it: "$5.00". */
export function formatOptionPrice(cents: number): string {
  const safe = Number.isFinite(cents) ? Math.max(0, Math.round(cents)) : 0;
  return `$${(safe / 100).toFixed(2)}`;
}

/** A modifier option's price delta: "+$4", or "—" when it adds nothing. */
export function formatPriceDelta(cents: number): string {
  return cents > 0 ? `+${formatPrice(cents)}` : "—";
}

const NUMBER_WORDS = [
  "zero",
  "one",
  "two",
  "three",
  "four",
  "five",
  "six",
  "seven",
  "eight",
  "nine",
  "ten",
  "eleven",
  "twelve",
];

/** "four" for 4; digits past twelve, where words stop reading naturally. */
export function partySizeInWords(partySize: number): string {
  const n = Math.max(0, Math.floor(partySize));
  return NUMBER_WORDS[n] ?? String(n);
}

/** "THE FOYR · PRE-ORDER" (uppercased by the eyebrow style). */
export function preorderEyebrow(restaurantName: string): string {
  return restaurantName ? `${restaurantName} · Pre-order` : "Pre-order";
}

/** "1 thing", "3 things". */
export function thingsLabel(count: number): string {
  return `${count} ${count === 1 ? "thing" : "things"}`;
}

/**
 * The sticky bar's "4× + 1×" breakdown of line quantities. Empty when every
 * line is a single, where the breakdown says nothing the count doesn't.
 */
export function quantityBreakdown(quantities: readonly number[]): string {
  if (!quantities.some((q) => q > 1)) return "";
  return quantities.map((q) => `${q}×`).join(" + ");
}

/** Splits "Wine list" into ["Wine", "list"] for the serif-italic first word. */
export function splitFirstWord(text: string): [string, string] {
  const trimmed = text.trim();
  const space = trimmed.indexOf(" ");
  if (space === -1) return [trimmed, ""];
  return [trimmed.slice(0, space), trimmed.slice(space + 1).trim()];
}

export interface ZonedDateParts {
  /** "Friday" */
  readonly weekday: string;
  /** "Fri" */
  readonly weekdayShort: string;
  /** "Aug" */
  readonly month: string;
  /** "7" */
  readonly day: string;
  /** "7:30", no meridiem — the hero's "Friday 7:30". */
  readonly hourMinute: string;
  /** "7:30 PM" */
  readonly time: string;
}

const partsFormatter = (timeZone: string | undefined) =>
  new Intl.DateTimeFormat("en-US", {
    weekday: "long",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone,
  });

const shortWeekdayFormatter = (timeZone: string | undefined) =>
  new Intl.DateTimeFormat("en-US", { weekday: "short", timeZone });

/**
 * The parts every V2 date label is built from, in `timeZone`. An unknown
 * zone falls back to the device zone rather than throwing; a missing or
 * invalid instant is null so callers can leave the label out.
 */
export function zonedDateParts(
  instant: Date | number | string | null | undefined,
  timeZone?: string,
): ZonedDateParts | null {
  if (instant === null || instant === undefined || instant === "") return null;
  const date = instant instanceof Date ? instant : new Date(instant);
  if (Number.isNaN(date.getTime())) return null;
  let zone = timeZone;
  let formatter: Intl.DateTimeFormat;
  try {
    formatter = partsFormatter(zone);
  } catch {
    zone = undefined;
    formatter = partsFormatter(zone);
  }
  const parts = formatter.formatToParts(date);
  const pick = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? "";
  const hourMinute = `${pick("hour")}:${pick("minute")}`;
  return {
    weekday: pick("weekday"),
    weekdayShort: shortWeekdayFormatter(zone).format(date),
    month: pick("month"),
    day: pick("day"),
    hourMinute,
    time: `${hourMinute} ${pick("dayPeriod").toUpperCase()}`.trim(),
  };
}
